import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, User, Mail, Phone, Calendar, MapPin, Heart, MessageSquare, Edit, Plus } from "lucide-react";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { contactsApi } from "@/api/contacts";
import { useToast } from "@/hooks/use-toast";
import { useRelationshipTypes, usePartialUpdateContact } from "@/hooks/api/useContacts";
import { useContactLogsByContactInfinite } from "@/hooks/api/useContactLogs";
import { useSuggestionsInfinite } from "@/hooks/api/useSuggestions";
import { AvatarSelector } from "@/components/contacts/AvatarSelector";
import { DatePicker } from "@/components/onboarding/DatePicker";
import AddMemoryModal from "@/components/memories/AddMemoryModal";
import MemoryCard from "@/components/memories/MemoryCard";
import SuggestionCard from "@/components/suggestions/SuggestionCard";
import { format } from "date-fns";
import type { Contact, RelationshipTypeEnum } from "@/types/api";
import { ContactDetailSkeleton } from "@/components/skeletons/ContactDetailSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { COUNTRIES } from "@/constants/countries";
import PersonalizeContactModal from "@/components/contacts/PersonalizeContactModal";

const ContactDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [showAddMemoryModal, setShowAddMemoryModal] = useState(false);
  const [memoryMoodFilter, setMemoryMoodFilter] = useState<string>("all");
  const [memorySortOrder, setMemorySortOrder] = useState<string>("-log_date");
  const [suggestionStatusFilter, setSuggestionStatusFilter] = useState<string>("all");
  const [suggestionSortOrder, setSuggestionSortOrder] = useState<string>("-created_at");

  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [avatar, setAvatar] = useState("");
  const [relationshipType, setRelationshipType] = useState<RelationshipTypeEnum | "">("");
  const [birthday, setBirthday] = useState<Date | undefined>(undefined);
  const [customFields, setCustomFields] = useState<Record<string, any>>({});

  const { data: relationshipTypes, isLoading: isLoadingTypes } = useRelationshipTypes();
  const updateContact = usePartialUpdateContact(parseInt(id || "0"));

  // Use infinite query hooks for memories and suggestions
  const {
    data: memoriesData,
    fetchNextPage: fetchNextMemories,
    hasNextPage: hasNextMemories,
    isFetchingNextPage: isFetchingNextMemories,
    isLoading: isLoadingMemories,
    refetch: refetchMemories
  } = useContactLogsByContactInfinite(parseInt(id || "0"), memorySortOrder, memoryMoodFilter);

  const {
    data: suggestionsData,
    fetchNextPage: fetchNextSuggestions,
    hasNextPage: hasNextSuggestions,
    isFetchingNextPage: isFetchingNextSuggestions,
    isLoading: isLoadingSuggestions
  } = useSuggestionsInfinite({
    ordering: suggestionSortOrder,
  });

  // Flatten paginated data
  const allMemories = memoriesData?.pages.flatMap(page => page.results) || [];
  const allSuggestions = suggestionsData?.pages.flatMap(page => page.results) || [];

  // Filter suggestions for this specific contact
  const contactSuggestions = allSuggestions.filter(s => s.contact === parseInt(id || "0"));

  // Fetch initial contact data
  useEffect(() => {
    const fetchContact = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const contactData = await contactsApi.get(parseInt(id));
        setContact(contactData);
      } catch (error) {
        console.error('Error fetching contact:', error);
        toast({
          title: "Error",
          description: "Failed to load contact details",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchContact();
  }, [id, toast]);

  // Initialize form fields when contact data is loaded
  useEffect(() => {
    if (!contact) return;

    setName(contact.name);
    setEmail(contact.email || "");
    setPhone(contact.phone || "");
    setCountry(contact.country || "");
    setAvatar(contact.avatar || "");
    setBirthday(contact.birthday ? new Date(contact.birthday) : undefined);

    if (contact.relationship_type) {
      setRelationshipType(contact.relationship_type as RelationshipTypeEnum);
    } else {
      setRelationshipType("");
    }

    // Merge relationship data and custom_data
    const mergedFields: Record<string, any> = {};
    if (contact.custom_data) {
      Object.keys(contact.custom_data).forEach(key => {
        mergedFields[key] = contact.custom_data[key];
      });
    }
    setCustomFields(mergedFields);
  }, [contact]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getDynamicFields = () => {
    if (!relationshipType || !relationshipTypes) return [];
    const typeData = relationshipTypes.find((t: any) => t.type === relationshipType);
    return typeData?.fields || [];
  };

  const formatLabel = (fieldName: string) => {
    return fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Filter suggestions by status (client-side for now)
  const filteredSuggestions = () => {
    if (suggestionStatusFilter === "all") return contactSuggestions;
    return contactSuggestions.filter(s => s.status === suggestionStatusFilter);
  };

  const handleSave = async () => {
    try {
      if (!name.trim()) {
        toast({
          title: "Name required",
          description: "Please enter a contact name",
          variant: "destructive",
        });
        return;
      }

      const dynamicFields = getDynamicFields();
      const requiredFields = dynamicFields.filter((field: any) => field.required);

      for (const field of requiredFields) {
        const value = customFields[field.name];
        if (!value && value !== false && value !== 0) {
          toast({
            title: "Missing required field",
            description: `Please fill in the ${field.name.replace(/_/g, " ")} field`,
            variant: "destructive",
          });
          return;
        }
      }

      const relationshipData: Record<string, any> = {};
      const otherCustomData: Record<string, any> = {};
      const dynamicFieldNames = new Set(dynamicFields.map((field: any) => field.name));

      Object.keys(customFields).forEach((key) => {
        const value = customFields[key];
        if (value !== "" && value !== null && value !== undefined) {
          if (dynamicFieldNames.has(key)) {
            relationshipData[key] = value;
          } else {
            otherCustomData[key] = value;
          }
        }
      });

      const updateData: any = { name: name.trim() };
      if (email) updateData.email = email.trim();
      if (phone) updateData.phone = phone.trim();
      if (country) updateData.country = country.trim();
      if (relationshipType) updateData.relationship_type = relationshipType;
      if (birthday) updateData.birthday = format(birthday, "yyyy-MM-dd");
      if (Object.keys(relationshipData).length > 0) updateData.relationship_data = relationshipData;
      if (Object.keys(otherCustomData).length > 0) updateData.custom_data = otherCustomData;

      await updateContact.mutateAsync(updateData);

      toast({
        title: "Contact updated",
        description: "Contact details have been saved successfully.",
      });

      // Refresh contact data
      const updatedContact = await contactsApi.get(parseInt(id || "0"));
      setContact(updatedContact);
      setIsEditing(false);
    } catch (error: any) {
      console.error("Error updating contact:", error);
      toast({
        title: "Error updating contact",
        description: error?.response?.data?.error || "Failed to update contact details.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    // Reset form to original contact data
    if (contact) {
      setName(contact.name);
      setEmail(contact.email || "");
      setPhone(contact.phone || "");
      setCountry(contact.country || "");
      setAvatar(contact.avatar || "");
      setBirthday(contact.birthday ? new Date(contact.birthday) : undefined);
      if (contact.relationship_type) {
        setRelationshipType(contact.relationship_type as RelationshipTypeEnum);
      } else {
        setRelationshipType("");
      }
      const mergedFields: Record<string, any> = {};
      if (contact.custom_data) {
        Object.keys(contact.custom_data).forEach(key => {
          mergedFields[key] = contact.custom_data[key];
        });
      }
      setCustomFields(mergedFields);
    }
    setIsEditing(false);
  };

  console.log('Contact data:', contact);

  if (loading) {
    return (
      <DashboardLayout>
        <ContactDetailSkeleton />
      </DashboardLayout>
    );
  }

  if (!contact) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 md:px-8">
          <div className="py-6 md:py-8">
            <div className="bg-white rounded-lg p-12 text-center">
              <p className="text-gray-500 mb-4">Contact not found</p>
              <Button onClick={() => navigate('/people')}>
                Back to People
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 md:px-8">
        <div className="py-6 md:py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/people')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to People
          </button>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Contact Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
                  {/* Avatar and Name Section */}
                  <div className="flex flex-col items-center mb-6">
                    <Avatar className="w-20 h-20 mb-4">
                      {contact.avatar ? (
                        <AvatarImage src={`/images/avatars/${contact.avatar}`} alt={contact.name} />
                      ) : null}
                      <AvatarFallback className="bg-gray-200">
                        <User className="w-10 h-10 text-gray-500" />
                      </AvatarFallback>
                    </Avatar>
                    <p className="text-[16px] font-bold text-black">{contact.name}</p>
                  </div>

                  {/* Contact Information */}
                  <div className="mb-6">
                    {/* Email */}
                    <div className="flex justify-between items-start pb-4 border-b border-gray-200">
                      <span className="text-[14px] text-[#8E8E8E]">Email</span>
                      <span className="text-[14px] text-black text-right break-words max-w-[60%]">
                        {contact.email || '-'}
                      </span>
                    </div>

                    {/* Phone */}
                    <div className="flex justify-between items-start py-4 border-b border-gray-200">
                      <span className="text-[14px] text-[#8E8E8E]">Phone</span>
                      <span className="text-[14px] text-black text-right">
                        {contact.phone || '-'}
                      </span>
                    </div>

                    {/* Country */}
                    <div className="flex justify-between items-start py-4 border-b border-gray-200">
                      <span className="text-[14px] text-[#8E8E8E]">Country</span>
                      <span className="text-[14px] text-black text-right">
                        {contact.country || '-'}
                      </span>
                    </div>

                    {/* Relationship */}
                    <div className="flex justify-between items-start py-4 border-b border-gray-200">
                      <span className="text-[14px] text-[#8E8E8E]">Relationship</span>
                      <span className="text-[14px] text-black text-right capitalize">
                        {contact.relationship_type ? contact.relationship_type.replace('_', ' ') : '-'}
                      </span>
                    </div>

                    {/* Birthday */}
                    <div className="flex justify-between items-start py-4 border-b border-gray-200">
                      <span className="text-[14px] text-[#8E8E8E]">Birthday</span>
                      <span className="text-[14px] text-black text-right">
                        {contact.birthday && formatDate(contact.birthday) ? formatDate(contact.birthday) : '-'}
                      </span>
                    </div>

                    {/* Custom Data */}
                    {contact.custom_data && Object.entries(contact.custom_data).map(([key, value]) => {
                      if (!value && value !== false) return null;
                      return (
                        <div key={key} className="flex justify-between items-start py-4 border-b border-gray-200">
                          <span className="text-[14px] text-[#8E8E8E] capitalize">{formatLabel(key)}</span>
                          <span className="text-[14px] text-black text-right max-w-[60%]">
                            {String(value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Quick Actions */}
                  <div className="space-y-2 mb-6">
                    {contact.email && (
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                        onClick={() => window.location.href = `mailto:${contact.email}`}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Send Email
                      </Button>
                    )}
                    {contact.phone && (
                      <>
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                          onClick={() => window.location.href = `tel:${contact.phone}`}
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          Call
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                          onClick={() => window.location.href = `sms:${contact.phone}`}
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Send Message
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Edit Button */}
                  <Button
                    onClick={() => setShowPersonalizeModal(true)}
                    className="w-full bg-wabisabi-plum hover:bg-wabisabi-plum/90 text-white"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Contact
                  </Button>
            </div>

            {/* Right Column - Tabs */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm">
                <Tabs defaultValue="memories" className="w-full">
                  <TabsList className="w-full justify-start border-b rounded-none bg-transparent px-6">
                    <TabsTrigger value="memories">Memories</TabsTrigger>
                    <TabsTrigger value="suggestions">Suggestions</TabsTrigger>
                    <TabsTrigger value="timeline">Timeline</TabsTrigger>
                  </TabsList>

                  <TabsContent value="memories" className="p-6">
                    {allMemories.length > 0 ? (
                      <>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-medium text-gray-700">Mood</label>
                              <Select value={memoryMoodFilter} onValueChange={setMemoryMoodFilter}>
                                <SelectTrigger className="w-[150px] h-9">
                                  <SelectValue placeholder="All Moods" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Moods</SelectItem>
                                  <SelectItem value="happy">Happy</SelectItem>
                                  <SelectItem value="sad">Sad</SelectItem>
                                  <SelectItem value="excited">Excited</SelectItem>
                                  <SelectItem value="calm">Calm</SelectItem>
                                  <SelectItem value="anxious">Anxious</SelectItem>
                                  <SelectItem value="grateful">Grateful</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-medium text-gray-700">Sort By</label>
                              <Select value={memorySortOrder} onValueChange={setMemorySortOrder}>
                                <SelectTrigger className="w-[150px] h-9">
                                  <SelectValue placeholder="Newest First" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="-log_date">Newest First</SelectItem>
                                  <SelectItem value="log_date">Oldest First</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <Button
                            onClick={() => setShowAddMemoryModal(true)}
                            className="bg-[#6C4F77] hover:bg-[#5a4063]"
                            size="sm"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Memory
                          </Button>
                        </div>
                        <div className="space-y-0">
                          {allMemories.map((log, index) => (
                            <MemoryCard
                              key={log.id}
                              memory={log}
                              variant="default"
                              showSeparator={index > 0}
                            />
                          ))}
                        </div>
                        {hasNextMemories && (
                          <div className="flex justify-center mt-6">
                            <Button
                              onClick={() => fetchNextMemories()}
                              disabled={isFetchingNextMemories}
                              variant="outline"
                            >
                              {isFetchingNextMemories ? (
                                <Skeleton className="h-4 w-20" />
                              ) : (
                                "Load More"
                              )}
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-500 mb-4">No memories yet</p>
                        <Button
                          onClick={() => setShowAddMemoryModal(true)}
                          className="bg-[#6C4F77] hover:bg-[#5a4063]"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add First Memory
                        </Button>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="suggestions" className="p-6">
                    {contactSuggestions.length > 0 ? (
                      <>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
                          <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Status</label>
                            <Select value={suggestionStatusFilter} onValueChange={setSuggestionStatusFilter}>
                              <SelectTrigger className="w-[150px] h-9">
                                <SelectValue placeholder="All Statuses" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="scheduled">Scheduled</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="dismissed">Dismissed</SelectItem>
                                <SelectItem value="expired">Expired</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Sort By</label>
                            <Select value={suggestionSortOrder} onValueChange={setSuggestionSortOrder}>
                              <SelectTrigger className="w-[180px] h-9">
                                <SelectValue placeholder="Newest First" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="-created_at">Newest First</SelectItem>
                                <SelectItem value="created_at">Oldest First</SelectItem>
                                <SelectItem value="-priority">Priority (High to Low)</SelectItem>
                                <SelectItem value="priority">Priority (Low to High)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {filteredSuggestions().map((suggestion) => (
                            <SuggestionCard key={suggestion.id} suggestion={suggestion} showCounts={true} showAvatar={false} />
                          ))}
                        </div>
                        {hasNextSuggestions && (
                          <div className="flex justify-center mt-6">
                            <Button
                              onClick={() => fetchNextSuggestions()}
                              disabled={isFetchingNextSuggestions}
                              variant="outline"
                            >
                              {isFetchingNextSuggestions ? (
                                <Skeleton className="h-4 w-20" />
                              ) : (
                                "Load More"
                              )}
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-500">No suggestions available yet</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="timeline" className="p-6">
                    <div className="text-center py-12">
                      <p className="text-gray-500">Timeline view coming soon</p>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Memory Modal */}
      <AddMemoryModal
        isOpen={showAddMemoryModal}
        onClose={() => {
          setShowAddMemoryModal(false);
          // Refresh memories after adding
          refetchMemories();
        }}
      />

      {/* Personalize Contact Modal */}
      {contact && (
        <PersonalizeContactModal
          isOpen={showPersonalizeModal}
          onClose={() => setShowPersonalizeModal(false)}
          contact={contact}
          onSuccess={async () => {
            setShowPersonalizeModal(false);
            // Refresh contact data
            try {
              const contactData = await contactsApi.get(parseInt(id!));
              setContact(contactData);
            } catch (error) {
              console.error('Error refreshing contact:', error);
            }
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default ContactDetail;
